<template>
    <div class="gestione-campi">

        <!-- Selettore modulo -->
        <v-card class="mb-4 elevation-1" rounded="lg">
            <v-card-text class="py-3">
                <v-row align="center" dense>

                    <v-col cols="12" sm="auto">
                        <span class="text-body-2 font-weight-medium text-medium-emphasis">
                            <v-icon size="16" class="me-1">mdi-view-module-outline</v-icon>
                            Modulo:
                        </span>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="moduloSelezionato" :items="moduli" item-title="label" item-value="value"
                            variant="outlined" density="compact" hide-details @update:model-value="caricaConfig">
                            <template #prepend-inner>
                                <v-icon size="16">mdi-layers-outline</v-icon>
                            </template>
                        </v-select>
                    </v-col>
                    <v-col cols="auto" class="ms-auto d-flex gap-2">
                        <v-btn size="small" variant="tonal" color="secondary" prepend-icon="mdi-message-plus-outline"
                            @click="apriDialogRichiesta" v-if="!isSuperAdmin" class="mr-2">
                            Richiedi nuovo campo
                        </v-btn>
                        <v-btn size="small" color="primary" prepend-icon="mdi-content-save-outline" :loading="salvando"
                            @click="salvaConfig">
                            Salva modifiche
                        </v-btn>
                    </v-col>

                </v-row>
            </v-card-text>
        </v-card>

        <!-- Legenda -->
        <v-card class="mb-4 elevation-0 border" rounded="lg">
            <v-card-text class="py-2 px-4">
                <v-row dense align="center">
                    <v-col cols="auto">
                        <span class="text-caption text-medium-emphasis font-weight-medium me-3">Legenda:</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="16" color="primary">mdi-eye-outline</v-icon>
                        <span class="text-caption">Visibile nel form</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="16" color="success">mdi-table-column</v-icon>
                        <span class="text-caption">Colonna tabella ordini</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="16" color="warning">mdi-pencil-outline</v-icon>
                        <span class="text-caption">Permetti valore libero</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="16" color="secondary">mdi-ruler-square</v-icon>
                        <span class="text-caption">Colonna/campo misure</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="16" color="grey">mdi-drag-vertical</v-icon>
                        <span class="text-caption">Trascina per ordinare</span>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <v-skeleton-loader v-for="i in 3" :key="i" type="card" class="mb-3" />
        </template>

        <!-- Sezioni con campi -->
        <template v-else>
            <v-card v-for="sezione in configurazione.sezioni" :key="sezione.id" class="mb-4 elevation-2" rounded="lg">

                <!-- Header sezione -->
                <v-card-title class="sezione-header px-4 py-3 d-flex align-center">
                    <v-icon :icon="sezione.icona || 'mdi-tune-variant'" size="18" class="me-2 text-medium-emphasis" />
                    <span class="text-body-2 font-weight-semibold">{{ sezione.etichetta }}</span>
                    <v-chip size="x-small" class="ms-2" variant="tonal" color="primary">
                        {{ campiVisibili(sezione) }}/{{ sezione.campi.length }} visibili
                    </v-chip>
                    <v-spacer />
                    <!-- toggle rapido sezione intera -->
                    <v-btn size="x-small" variant="text" :color="tuttiVisibili(sezione) ? 'error' : 'primary'"
                        @click="toggleSezione(sezione)">
                        {{ tuttiVisibili(sezione) ? 'Nascondi tutti' : 'Mostra tutti' }}
                    </v-btn>
                </v-card-title>

                <v-divider />

                <!-- Header colonne tabella -->
                <div class="campi-table-header">
                    <div class="col-drag"></div>
                    <div class="col-campo">Campo</div>
                    <div class="col-tipo">Tipo</div>
                    <div class="col-toggle text-center">
                        <v-icon size="14" color="primary">mdi-eye-outline</v-icon>
                        <span class="ms-1">Form</span>
                    </div>
                    <div class="col-toggle text-center">
                        <v-icon size="14" color="success">mdi-table-column</v-icon>
                        <span class="ms-1">Tab. ordini</span>
                    </div>
                    <div class="col-toggle text-center">
                        <v-icon size="14" color="warning">mdi-pencil-outline</v-icon>
                        <span class="ms-1">Val. libero</span>
                    </div>
                    <div class="col-toggle text-center" v-if="sezione.codice === 'misure'">
                        <v-icon size="14" color="secondary">mdi-ruler-square</v-icon>
                        <span class="ms-1">Tab. misure</span>
                    </div>
                </div>

                <!-- Drag & drop lista campi -->
                <draggable v-model="sezione.campi" item-key="id" handle=".drag-handle" ghost-class="drag-ghost"
                    chosen-class="drag-chosen" @end="onDragEnd(sezione)">
                    <template #item="{ element: campo }">
                        <div class="campo-row" :class="{ 'campo-nascosto': !campo.visibile }">

                            <!-- Handle drag -->
                            <div class="col-drag">
                                <v-icon class="drag-handle" size="18" color="grey-lighten-1">
                                    mdi-drag-vertical
                                </v-icon>
                            </div>

                            <!-- Nome campo -->
                            <div class="col-campo">
                                <div class="text-body-2 font-weight-medium" :class="campo.visibile ? '' : 'text-grey'">
                                    {{ campo.etichetta }}
                                </div>
                                <div class="text-caption text-medium-emphasis">{{ campo.campoKey }}</div>
                            </div>

                            <!-- Tipo input badge -->
                            <div class="col-tipo">
                                <v-chip :color="coloreTipoInput(campo.tipoInput)" size="x-small" variant="tonal">
                                    <v-icon start size="10">{{ iconaTipoInput(campo.tipoInput) }}</v-icon>
                                    {{ campo.tipoInput }}
                                </v-chip>
                                <v-chip v-if="campo.obbligatorio" color="error" size="x-small" variant="tonal"
                                    class="ms-1">
                                    req.
                                </v-chip>
                            </div>

                            <!-- Toggle: visibile nel form -->
                            <div class="col-toggle d-flex justify-center">
                                <v-switch v-model="campo.visibile" color="primary" density="compact" hide-details
                                    class="toggle-compact" />
                            </div>

                            <!-- Toggle: colonna tabella ordini -->
                            <div class="col-toggle d-flex justify-center">
                                <v-switch v-model="campo.visibileTabella" color="success" density="compact" hide-details
                                    class="toggle-compact" :disabled="!campo.visibile" />
                            </div>

                            <!-- Toggle: permetti valore custom -->
                            <div class="col-toggle d-flex justify-center">
                                <v-switch
                                    v-if="['select', 'autocomplete', 'radio', 'checkbox', 'checkboxgroup'].includes(campo.tipoInput)"
                                    v-model="campo.permettiValoreCustom" color="warning" density="compact" hide-details
                                    class="toggle-compact" :disabled="!campo.visibile" />
                                <span v-else class="text-caption text-grey">—</span>
                            </div>

                            <!-- Toggle: tabella/dialog misure (solo sezione misure) -->
                            <div class="col-toggle d-flex justify-center" v-if="sezione.codice === 'misure'">
                                <v-switch v-model="campo.visibileMisure" color="secondary" density="compact"
                                    hide-details class="toggle-compact" :disabled="!campo.visibile" />
                            </div>

                        </div>
                    </template>
                </draggable>

            </v-card>
        </template>

        <!-- Sezione misure separata: campi misura_* -->
        <v-card v-if="!loading && campiFiltrati('misure').length > 0" class="mb-4 elevation-2" rounded="lg">
            <v-card-title class="sezione-header px-4 py-3 d-flex align-center">
                <v-icon icon="mdi-ruler-square" size="18" class="me-2 text-medium-emphasis" />
                <span class="text-body-2 font-weight-semibold">Campi Tabella Misure</span>
                <v-chip size="x-small" class="ms-2" variant="tonal" color="secondary">
                    {{campiFiltrati('misure').filter(c => c.visibileMisure).length}}/{{
                        campiFiltrati('misure').length }} visibili
                </v-chip>
            </v-card-title>
            <v-divider />
            <div class="campi-table-header">
                <div class="col-drag"></div>
                <div class="col-campo">Campo</div>
                <div class="col-tipo">Tipo</div>
                <div class="col-toggle text-center">
                    <v-icon size="14" color="secondary">mdi-ruler-square</v-icon>
                    <span class="ms-1">Visibile</span>
                </div>
            </div>
            <draggable v-model="campiMisure" item-key="id" handle=".drag-handle" ghost-class="drag-ghost"
                chosen-class="drag-chosen" @end="onDragEndMisure">
                <template #item="{ element: campo }">
                    <div class="campo-row" :class="{ 'campo-nascosto': !campo.visibileMisure }">
                        <div class="col-drag">
                            <v-icon class="drag-handle" size="18" color="grey-lighten-1">mdi-drag-vertical</v-icon>
                        </div>
                        <div class="col-campo">
                            <div class="text-body-2 font-weight-medium"
                                :class="campo.visibileMisure ? '' : 'text-grey'">
                                {{ campo.etichetta }}
                            </div>
                            <div class="text-caption text-medium-emphasis">{{ campo.campoKey }}</div>
                        </div>
                        <div class="col-tipo">
                            <v-chip :color="coloreTipoInput(campo.tipoInput)" size="x-small" variant="tonal">
                                <v-icon start size="10">{{ iconaTipoInput(campo.tipoInput) }}</v-icon>
                                {{ campo.tipoInput }}
                            </v-chip>
                        </div>
                        <div class="col-toggle d-flex justify-center">
                            <v-switch v-model="campo.visibileMisure" color="secondary" density="compact" hide-details
                                class="toggle-compact" />
                        </div>
                    </div>
                </template>
            </draggable>
        </v-card>

        <!-- Storico richieste -->
        <v-card v-if="!loading" class="mb-4 elevation-1" rounded="lg">
            <v-card-title class="sezione-header px-4 py-3 d-flex align-center">
                <v-icon icon="mdi-message-text-outline" size="18" class="me-2 text-medium-emphasis" />
                <span class="text-body-2 font-weight-semibold">Le tue richieste di modifica</span>
                <v-btn size="x-small" variant="text" class="ms-auto" @click="caricaRichieste">
                    <v-icon size="14">mdi-refresh</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-0">
                <div v-if="richieste.length === 0" class="text-center pa-4 text-caption text-grey">
                    Nessuna richiesta inviata
                </div>
                <div v-for="r in richieste" :key="r.id" class="richiesta-row px-4 py-3">
                    <div class="d-flex align-start gap-3">
                        <v-chip :color="coloreStato(r.stato)" size="x-small" variant="tonal" class="mt-1 flex-shrink-0">
                            {{ r.stato }}
                        </v-chip>
                        <div class="flex-1">
                            <div class="text-body-2">{{ r.testo }}</div>
                            <div v-if="r.rispostaDev" class="text-caption text-medium-emphasis mt-1">
                                <strong>Risposta:</strong> {{ r.rispostaDev }}
                            </div>
                            <div class="text-caption text-grey mt-1">
                                {{ formatData(r.dataCreazione) }} — {{ r.modulo }}
                            </div>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Dialog: richiesta nuovo campo -->
        <v-dialog v-model="dialogRichiesta" max-width="560" persistent>
            <v-card rounded="lg">
                <v-card-title class="dialog-header pa-4 pb-2 d-flex align-center">
                    <v-icon icon="mdi-message-plus-outline" color="secondary" size="18" class="me-2" />
                    <span class="text-body-1 font-weight-semibold">Richiedi un nuovo campo</span>
                    <v-spacer />
                    <v-btn icon size="small" variant="text" @click="dialogRichiesta = false">
                        <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-4">
                    <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                        Descrivi il campo che ti serve: nome, dove dovrebbe comparire nel form,
                        che tipo di dato contiene e come vorresti inserirlo (testo, lista, ecc.).
                        Il nostro team valuterà la fattibilità e ti risponderà.
                    </v-alert>
                    <v-select v-model="richiestaForm.modulo" :items="moduli" item-title="label" item-value="value"
                        label="Modulo di riferimento" variant="outlined" density="compact" class="mb-3" />
                    <v-textarea v-model="richiestaForm.testo" label="Descrizione della richiesta" variant="outlined"
                        density="compact" :rows="5" autoGrow :rules="[v => !!v || 'Campo obbligatorio']"
                        placeholder="Es: Vorrei un campo 'Colore Telaio' nella sezione Specifiche Tecniche, con un elenco di colori selezionabili..." />
                </v-card-text>
                <v-divider />
                <v-card-actions class="pa-3">
                    <v-spacer />
                    <v-btn variant="text" @click="dialogRichiesta = false">Annulla</v-btn>
                    <v-btn color="secondary" variant="flat" :loading="inviandoRichiesta"
                        :disabled="!richiestaForm.testo" @click="inviaRichiesta">
                        Invia richiesta
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="3000">
            {{ snackbar.text }}
        </v-snackbar>

    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import draggable from 'vuedraggable'
import {
    get_configurazione_admin,
    update_configurazione_batch,
    crea_richiesta,
    get_richieste_mie,
} from '@/api/config/api.campi.js'

export default {
    name: 'GestioneCampiForm',
    components: { draggable },

    props: {
        // Passato dal parent (GestioneParametri) se il superadmin ha selezionato un'azienda
        aziendaIdEsterno: { type: Number, default: null }
    },

    setup(props) {
        const store = useStore()
        const isSuperAdmin = computed(() => store.getters['user/getRole'] === 3)
        const loading = ref(false)
        const salvando = ref(false)
        const inviandoRichiesta = ref(false)
        const dialogRichiesta = ref(false)

        const moduli = [
            { label: 'Serramenti', value: 'serramenti' },
            /* { label: 'Persiane', value: 'persiane' },
            { label: 'Porte Interne', value: 'porte_interne' } */
        ]

        const moduloSelezionato = ref('serramenti')

        // Configurazione caricata dal backend
        const configurazione = ref({ modulo: 'serramenti', sezioni: [] })

        // Campi misure separati (campoKey inizia con 'misura_')
        const campiMisure = ref([])

        const richieste = ref([])

        const richiestaForm = ref({ modulo: 'serramenti', testo: '' })

        const snackbar = ref({ show: false, text: '', color: 'success' })
        const notifica = (text, color = 'success') => { snackbar.value = { show: true, text, color } }

        // ── Caricamento ────────────────────────────────────────────────────────
        const caricaConfig = async () => {
            loading.value = true
            try {
                const res = await get_configurazione_admin(moduloSelezionato.value, props.aziendaIdEsterno)
                const data = res.data

                // Separo i campi misura_* dalle sezioni normali
                const sezioniFiltrate = []
                const misure = []

                for (const sezione of (data.sezioni || [])) {
                    const campiNormali = sezione.campi.filter(c => !c.campoKey.startsWith('misura_'))
                    const campiMis = sezione.campi.filter(c => c.campoKey.startsWith('misura_'))

                    if (campiNormali.length > 0) {
                        sezioniFiltrate.push({ ...sezione, campi: campiNormali })
                    }
                    misure.push(...campiMis)
                }

                configurazione.value = { modulo: data.modulo, sezioni: sezioniFiltrate }
                campiMisure.value = misure.sort((a, b) => a.ordinamento - b.ordinamento)

            } catch {
                notifica('Errore nel caricamento della configurazione', 'error')
            } finally {
                loading.value = false
            }
        }

        const caricaRichieste = async () => {
            try {
                const res = await get_richieste_mie()
                richieste.value = res.data || []
            } catch { /* silenzioso */ }
        }

        // ── Salvataggio ────────────────────────────────────────────────────────
        const salvaConfig = async () => {
            salvando.value = true
            try {
                // Raccolgo tutti i campi da tutte le sezioni + misure
                const campiPayload = []

                configurazione.value.sezioni.forEach(sezione => {
                    sezione.campi.forEach((campo, idx) => {
                        campiPayload.push({
                            id: campo.id,
                            visibile: campo.visibile,
                            visibileTabella: campo.visibileTabella,
                            visibileMisure: campo.visibileMisure,
                            permettiValoreCustom: campo.permettiValoreCustom ?? false,
                            ordinamento: (idx + 1) * 10
                        })
                    })
                })

                campiMisure.value.forEach((campo, idx) => {
                    campiPayload.push({
                        id: campo.id,
                        visibile: campo.visibile,
                        visibileTabella: campo.visibileTabella,
                        visibileMisure: campo.visibileMisure,
                        permettiValoreCustom: campo.permettiValoreCustom ?? false,
                        ordinamento: (idx + 1) * 10
                    })
                })
                console.log(props.aziendaIdEsterno);
                await update_configurazione_batch(moduloSelezionato.value, campiPayload, props.aziendaIdEsterno)
                notifica('Configurazione salvata con successo')
            } catch {
                notifica('Errore durante il salvataggio', 'error')
            } finally {
                salvando.value = false
            }
        }

        // ── Richiesta nuovo campo ──────────────────────────────────────────────
        const apriDialogRichiesta = () => {
            richiestaForm.value = { modulo: moduloSelezionato.value, testo: '' }
            dialogRichiesta.value = true
        }

        const inviaRichiesta = async () => {
            if (!richiestaForm.value.testo) return
            inviandoRichiesta.value = true
            try {
                await crea_richiesta(richiestaForm.value)
                dialogRichiesta.value = false
                notifica('Richiesta inviata con successo')
                await caricaRichieste()
            } catch {
                notifica('Errore durante l\'invio', 'error')
            } finally {
                inviandoRichiesta.value = false
            }
        }

        // ── Helpers sezione ────────────────────────────────────────────────────
        const campiVisibili = (sezione) => sezione.campi.filter(c => c.visibile).length
        const tuttiVisibili = (sezione) => sezione.campi.every(c => c.visibile)

        const toggleSezione = (sezione) => {
            const nuovoStato = !tuttiVisibili(sezione)
            sezione.campi.forEach(c => { c.visibile = nuovoStato })
        }

        const campiFiltrati = (tipo) => {
            if (tipo === 'misure') return campiMisure.value
            return []
        }

        // ── Drag end: aggiorna ordinamento locale ──────────────────────────────
        const onDragEnd = (sezione) => {
            sezione.campi.forEach((c, idx) => { c.ordinamento = (idx + 1) * 10 })
        }

        const onDragEndMisure = () => {
            campiMisure.value.forEach((c, idx) => { c.ordinamento = (idx + 1) * 10 })
        }

        // ── Utils display ──────────────────────────────────────────────────────
        const coloreTipoInput = (tipo) => ({
            text: 'primary', number: 'secondary', textarea: 'info',
            select: 'warning', autocomplete: 'warning', checkbox: 'success',
            checkboxgroup: 'success', radio: 'error', date: 'purple'
        }[tipo] || 'grey')

        const iconaTipoInput = (tipo) => ({
            text: 'mdi-form-textbox', number: 'mdi-numeric',
            textarea: 'mdi-form-textarea', select: 'mdi-chevron-down-box-outline',
            autocomplete: 'mdi-magnify', checkbox: 'mdi-checkbox-marked-outline',
            checkboxgroup: 'mdi-checkbox-multiple-marked-outline',
            radio: 'mdi-radiobox-marked', date: 'mdi-calendar-outline'
        }[tipo] || 'mdi-form-textbox')

        const coloreStato = (stato) => ({
            nuova: 'warning', in_lavorazione: 'info', chiusa: 'success'
        }[stato] || 'grey')

        const formatData = (d) => {
            if (!d) return ''
            return new Date(d).toLocaleDateString('it-IT', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })
        }

        onMounted(async () => {
            await Promise.all([caricaConfig(), caricaRichieste()]);
        })

        watch(() => props.aziendaIdEsterno, () => { caricaConfig() })

        return {
            loading, salvando, inviandoRichiesta, dialogRichiesta,
            moduli, moduloSelezionato,
            configurazione, campiMisure, richieste,
            richiestaForm, snackbar,
            caricaConfig, caricaRichieste, salvaConfig,
            apriDialogRichiesta, inviaRichiesta,
            campiVisibili, tuttiVisibili, toggleSezione, campiFiltrati,
            onDragEnd, onDragEndMisure,
            coloreTipoInput, iconaTipoInput, coloreStato, formatData,
            isSuperAdmin
        }
    }
}
</script>

<style scoped>
/* ── Header sezione ── */
.sezione-header {
    background: #f7f7f8;
    border-radius: 12px 12px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, .06);
    min-height: 48px;
}

/* ── Tabella campi ── */
.campi-table-header {
    display: grid;
    grid-template-columns: 36px 1fr 160px 100px 120px 100px;
    padding: 6px 16px;
    background: #f6f7f9;
    border-bottom: 1px solid rgba(0, 0, 0, .08);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .5px;
    color: #6b7280;
    gap: 8px;
}

.campo-row {
    display: grid;
    grid-template-columns: 36px 1fr 160px 100px 120px 100px;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, .05);
    transition: background .15s ease;
    gap: 8px;
    min-height: 52px;
}

.campo-row:last-child {
    border-bottom: none;
}

.campo-row:hover {
    background: #f0f4f8;
}

.campo-nascosto {
    opacity: .5;
    background: #fafafa;
}

.campo-nascosto:hover {
    background: #f3f4f6;
}

/* ── Colonne ── */
.col-drag {
    display: flex;
    align-items: center;
}

.col-campo {
    overflow: hidden;
}

.col-tipo {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.col-toggle {
    min-width: 80px;
}

/* ── Drag ── */
.drag-handle {
    cursor: grab;
}

.drag-handle:active {
    cursor: grabbing;
}

.drag-ghost {
    opacity: .4;
    background: #e0e7ff !important;
}

.drag-chosen {
    background: #eff6ff !important;
    box-shadow: 0 4px 12px rgba(30, 58, 95, .12);
}

/* ── Toggle compatto ── */
.toggle-compact :deep(.v-switch__track) {
    height: 16px;
    width: 32px;
}

.toggle-compact :deep(.v-switch__thumb) {
    width: 12px;
    height: 12px;
}

/* ── Richieste ── */
.richiesta-row {
    border-bottom: 1px solid rgba(0, 0, 0, .05);
}

.richiesta-row:last-child {
    border-bottom: none;
}

/* ── Dialog header ── */
.dialog-header {
    background: #f7f7f8;
    border-radius: 12px 12px 0 0;
}

/* ── Responsive: mobile ── */
@media (max-width: 600px) {

    .campi-table-header,
    .campo-row {
        grid-template-columns: 32px 1fr 80px 64px;
    }

    /* nascondi colonne tab. ordini e val. libero su mobile */
    .campi-table-header>*:nth-child(5),
    .campo-row>*:nth-child(5),
    .campi-table-header>*:nth-child(6),
    .campo-row>*:nth-child(6) {
        display: none;
    }
}
</style>